import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiRequest } from '../api/client'

const ACTION_STYLES = {
  'trade_application.submitted':    'bg-emerald-50 text-emerald-700 border-emerald-200',
  'trade_application.viewed':       'bg-slate-50 text-slate-700 border-slate-200',
  'user.signed_in_with_google':     'bg-brand-50 text-brand-700 border-brand-200',
  'user.signed_out':                'bg-slate-50 text-slate-700 border-slate-200',
  'user.sign_in_rejected':          'bg-rose-50 text-rose-700 border-rose-200',
  'user.created':                   'bg-amber-50 text-amber-800 border-amber-200',
  'user.auto_created_via_google':   'bg-emerald-50 text-emerald-700 border-emerald-200',
}

function ActionBadge({ action }) {
  const cls = ACTION_STYLES[action] || 'bg-slate-50 text-slate-700 border-slate-200'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${cls}`}>
      {action}
    </span>
  )
}

function TargetLink({ type, id }) {
  if (!type || !id) return <span className="text-slate-400">—</span>
  if (type === 'TradeApplication') {
    return (
      <Link to={`/trade_applications/${id}`} className="text-brand-600 hover:text-brand-700">
        TradeApplication #{id}
      </Link>
    )
  }
  return <span className="text-slate-600">{type} #{id}</span>
}

export default function AdminActivityPage() {
  const [activities, setActivities] = useState([])
  const [actions, setActions] = useState([])
  const [actionFilter, setActionFilter] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = () => {
    setLoading(true)
    const qs = new URLSearchParams()
    if (actionFilter) qs.set('action_filter', actionFilter)
    if (page > 1) qs.set('page', page)
    apiRequest(`/api/v1/admin/activities?${qs}`)
      .then((data) => {
        setActivities(data.activities)
        setActions(data.actions)
        setTotal(data.total)
        setPageSize(data.page_size)
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(load, [actionFilter, page])

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Activity log</h1>
        <p className="text-sm text-slate-600 mt-1">
          Every operation performed across the platform.
        </p>
      </div>

      <div className="card p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="label" htmlFor="action-filter">Filter by action</label>
          <select
            id="action-filter"
            value={actionFilter}
            onChange={(e) => { setPage(1); setActionFilter(e.target.value) }}
            className="input"
          >
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="text-sm text-slate-600 pb-2">
          {total.toLocaleString()} total event{total === 1 ? '' : 's'}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-600">Loading…</p>
      ) : error ? (
        <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">{error}</div>
      ) : activities.length === 0 ? (
        <div className="card p-8 text-center text-slate-600">No activity yet.</div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">When</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {activities.map((a) => (
                    <tr key={a.id} className="align-top">
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                        {new Date(a.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3"><ActionBadge action={a.action} /></td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                        {a.actor || <span className="text-slate-400 italic">anonymous</span>}
                        {a.actor_tombstoned && (
                          <span className="ml-1 text-xs text-slate-400 italic">(deleted user)</span>
                        )}
                        {a.ip_address && (
                          <div className="text-xs text-slate-400 font-mono">{a.ip_address}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <TargetLink type={a.target_type} id={a.target_id} />
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {a.metadata && Object.keys(a.metadata).length > 0 ? (
                          <dl className="space-y-0.5">
                            {Object.entries(a.metadata).map(([k, v]) => (
                              <div key={k} className="flex gap-1">
                                <dt className="text-slate-400">{k}:</dt>
                                <dd className="text-slate-700 break-all">{String(v)}</dd>
                              </div>
                            ))}
                          </dl>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-slate-600">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button
                  className="btn-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn-secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
